import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardMainContent from '../components/layout/DashboardMainContent';
import SideNavBar from '../components/layout/SideNavBar';
import { courseService } from '../services/courseService';
import { useAuthStore } from '../stores/authStore';
import '../styles/Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout, isAuthenticated } = useAuthStore();
  const [activeTab, setActiveTab] = useState('all-courses');
  const [courses, setCourses] = useState([]);
  const [purchasedCourses, setPurchasedCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Fetch courses and purchased content on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) return;

      try {
        setIsLoading(true);

        // Fetch all available courses first — this must always succeed
        const coursesResponse = await courseService.getCourses();

        // coursesResponse = { success, data: { message, data: [...], count } }
        // unwrap through both withErrorHandling wrapper and API envelope
        const coursesList =
          coursesResponse?.data?.data ??
          (Array.isArray(coursesResponse?.data) ? coursesResponse.data : null) ??
          (Array.isArray(coursesResponse) ? coursesResponse : []);

        if (Array.isArray(coursesList) && coursesList.length > 0) {
          const formattedCourses = coursesList.map(course => ({
            id: course.id,
            title: course.title,
            description: course.description,
            originalPrice: course.originalPrice,
            offerPrice: course.offerPrice,
            image: course.displayPicture,
            tags: course.tags,
            price: `₹${course.offerPrice}`,
            duration: '4 months',
            isPurchased: false,
          }));
          setCourses(formattedCourses);
        }
      } catch (error) {
        console.error('Error fetching courses:', error);
        toast.error('Failed to load courses. Please try again later.');
      } finally {
        setIsLoading(false);
      }

      // Fetch purchased courses separately — a 401 here must NOT block the courses above
      try {
        const purchasedResponse = await courseService.getPurchasedCourses(currentUser.id);
        if (purchasedResponse) {
          const purchasedItems = purchasedResponse
            .filter(item => item !== null && item !== undefined)
            .flat();
          setPurchasedCourses(purchasedItems);

          if (location.state?.paymentSuccess) {
            const itemCount = location.state.purchasedItems?.length || 0;
            if (itemCount > 0) {
              const itemType = location.state.isFullCourse ? 'course' : 'topic';
              const itemText = itemCount > 1 ? `${itemCount} ${itemType}s` : `${itemCount} ${itemType}`;
              toast.success(`Successfully purchased ${itemText}!`);
            }
          }
        }
      } catch (error) {
        // 401 is expected when the token doesn't have purchase access — silently ignore
        console.warn('Could not load purchased courses:', error.message);
      }
    };

    fetchData();
  }, [isAuthenticated, currentUser?.id, location.state]);

  // Handle tab change - navigate to different routes
  const handleTabChange = (tabId) => {
    if (tabId === 'home') {
      setActiveTab('home');
    } else if (tabId === 'all-courses') {
      setActiveTab('all-courses');
    } else if (tabId === 'profile') {
      navigate('/profile');
    }
    // Add more navigation logic for future tabs
  };

  const handleLogout = async () => {
    try {
      console.log('🔄 Dashboard logout initiated');
      await logout();
      console.log('✅ Dashboard logout successful');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('❌ Dashboard logout failed:', error);
      // Show error to user - they remain logged in
      toast.error(error.message || 'Logout failed. Please try again.');
    }
  };

  const handleCourseClick = (courseId) => {
    navigate(`/course/${courseId}`);
  };



  return (
    <div className="dashboard-container">
      <SideNavBar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onLogout={handleLogout}
        currentUser={currentUser}
      />
      <DashboardMainContent
        activeTab={activeTab}
        courses={courses}
        purchasedCourses={purchasedCourses}
        isLoading={isLoading}
        onCourseClick={handleCourseClick}
      />
    </div>
  );
};

export default Dashboard;
